"use client";

import * as React from "react";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";

import { CONTROL_HEIGHT_CLASSES } from "@/lib/design-system/control-density";
import { cn } from "@/lib/utils";

const segmentedControlVariants = cva(
  "inline-flex w-fit items-center max-sm:max-w-full max-sm:overflow-x-auto max-sm:[scrollbar-width:none] sm:flex-wrap",
  {
    variants: {
      size: {
        default: "gap-2",
        sm: "gap-1.5",
        compact: "gap-1.5",
      },
      variant: {
        outline: "max-sm:-my-1 max-sm:py-1",
        track: "gap-0.5 rounded-lg bg-border-subtle p-0.5",
        native:
          "max-md:w-full max-md:flex-nowrap max-md:gap-0.5 max-md:rounded-lg max-md:bg-border-subtle max-md:p-0.5",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "outline",
    },
  },
);

const segmentedControlItemVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-md border font-medium whitespace-nowrap text-fg-muted outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 data-[state=off]:hover:text-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        outline:
          "border-border bg-card data-[state=off]:hover:border-border-strong data-[state=off]:hover:bg-surface-2 data-[state=on]:border-action data-[state=on]:bg-action-tint data-[state=on]:font-semibold data-[state=on]:text-action-strong",
        track:
          "border-transparent bg-transparent data-[state=on]:border-border data-[state=on]:bg-card data-[state=on]:text-foreground",
        native: [
          "md:border-border md:bg-card md:data-[state=off]:hover:border-border-strong md:data-[state=off]:hover:bg-surface-2 md:data-[state=on]:border-action md:data-[state=on]:bg-action-tint md:data-[state=on]:font-semibold md:data-[state=on]:text-action-strong",
          "max-md:h-10 max-md:min-w-0 max-md:flex-1 max-md:border-transparent max-md:bg-transparent max-md:text-base max-md:data-[state=on]:border-border max-md:data-[state=on]:bg-card max-md:data-[state=on]:font-semibold max-md:data-[state=on]:text-foreground max-md:data-[state=on]:shadow-sm",
        ].join(" "),
      },
      size: {
        default: `${CONTROL_HEIGHT_CLASSES.default} px-3 text-sm`,
        sm: `${CONTROL_HEIGHT_CLASSES.sm} px-3 text-sm`,
        compact: `${CONTROL_HEIGHT_CLASSES.compact} px-2.5 text-xs`,
      },
    },
    defaultVariants: {
      size: "default",
      variant: "outline",
    },
  },
);

type SegmentSize = VariantProps<typeof segmentedControlItemVariants>["size"];
type SegmentVariant = VariantProps<typeof segmentedControlItemVariants>["variant"];

const SegmentedControlContext = React.createContext<{
  size: SegmentSize;
  variant: SegmentVariant;
  fullWidth: boolean;
}>({
  size: "default",
  variant: "outline",
  fullWidth: false,
});

export interface SegmentedControlOption<TValue extends string> {
  label: React.ReactNode;
  value: TValue;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  disabled?: boolean;
  title?: string;
  "aria-label"?: string;
}

interface SegmentedControlProps<TValue extends string> extends VariantProps<
  typeof segmentedControlVariants
> {
  value: TValue;
  onValueChange?: (value: TValue) => void;
  onOptionClick?: (value: TValue) => void;
  options: SegmentedControlOption<TValue>[];
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

function SegmentedControl<TValue extends string>({
  value,
  onValueChange,
  onOptionClick,
  options,
  size = "default", variant = "outline", disabled,
  fullWidth = false, className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
}: SegmentedControlProps<TValue>) {
  return (
    <SegmentedControlContext value={{ size, variant, fullWidth }}>
      <ToggleGroupPrimitive.Root
        type="single"
        value={value}
        onValueChange={(next) => {
          if (onOptionClick) return;
          const selected = options.find((option) => option.value === next);
          if (selected) onValueChange?.(selected.value);
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        className={cn(
          segmentedControlVariants({ size, variant }),
          fullWidth && "flex w-full flex-nowrap",
          className,
        )}
      >
        {options.map((option) => (
          <SegmentedControlItem
            key={option.value}
            option={option}
            onOptionClick={onOptionClick}
          />
        ))}
      </ToggleGroupPrimitive.Root>
    </SegmentedControlContext>
  );
}

function SegmentBadge({ badge }: { badge: React.ReactNode }) {
  if (badge === "" || badge === null || badge === undefined) return null;
  if (typeof badge !== "string" && typeof badge !== "number") return badge;
  return (
    <>
      {" "}
      <span className="font-normal tabular-nums text-muted-foreground">{badge}</span>
    </>
  );
}

function SegmentedControlItem<TValue extends string>({
  option,
  onOptionClick,
}: {
  option: SegmentedControlOption<TValue>;
  onOptionClick?: (value: TValue) => void;
}) {
  const { size, variant, fullWidth } = React.use(SegmentedControlContext);
  return (
    <ToggleGroupPrimitive.Item
      data-slot="segmented-control-item"
      value={option.value}
      disabled={option.disabled}
      title={option.title}
      aria-label={option["aria-label"]}
      onClick={onOptionClick ? () => onOptionClick(option.value) : undefined}
      className={cn(
        segmentedControlItemVariants({ size, variant }),
        fullWidth && "min-w-0 flex-1 whitespace-normal",
      )}
    >
      {option.icon}
      <span>{option.label}</span>
      <SegmentBadge badge={option.badge} />
    </ToggleGroupPrimitive.Item>
  );
}

export { SegmentedControl, segmentedControlItemVariants };
